import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Leaf, AlertTriangle, ArrowRight, Lock, LogIn, User as UserIcon } from "lucide-react";
import { analyzeMedications } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import MedicationInput from "@/components/MedicationInput";
import ResultsBento from "@/components/ResultsBento";
import Disclaimer from "@/components/Disclaimer";
import AuthModal from "@/components/AuthModal";

export default function HomePage() {
  const [meds, setMeds] = useState([]);
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [paywall, setPaywall] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);
  const [authMode, setAuthMode] = useState("signin");
  const { user } = useAuth();

  const handleAnalyze = async () => {
    if (meds.length === 0) { toast.error("Add at least one medication first."); return; }
    setLoading(true);
    setResult(null);
    try {
      const data = await analyzeMedications(meds, notes);
      setResult(data);
      setTimeout(() => document.getElementById("results")?.scrollIntoView({ behavior: "smooth" }), 200);
    } catch (e) {
      if (e?.response?.status === 402) {
        setPaywall(true);
      } else {
        toast.error(e?.response?.data?.detail || e?.message || "Something went wrong.");
      }
    } finally { setLoading(false); }
  };

  const handleReset = () => { setResult(null); setMeds([]); setNotes(""); window.scrollTo({ top: 0, behavior: "smooth" }); };

  return (
    <div className="relative z-10 min-h-screen" data-testid="home-page">
      {/* Top bar */}
      <header className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 sm:pt-8 flex items-center justify-between gap-3">
        <Link to="/" className="flex items-center gap-2 flex-shrink-0" data-testid="brand-logo">
          <span className="inline-flex h-9 w-9 items-center justify-center rounded-full" style={{ background: "var(--brand-light)" }}>
            <Leaf className="h-4 w-4" style={{ color: "var(--brand)" }} strokeWidth={1.5} />
          </span>
          <span className="font-serif-display text-2xl tracking-tight" style={{ color: "var(--text-primary)" }}>SafeRxCheck</span>
        </Link>
        <nav className="flex items-center gap-2 sm:gap-3">
          <Link to="/pricing" className="hidden sm:inline label-small hover:opacity-70" data-testid="nav-pricing">PRICING</Link>
          {user ? (
            <Link to="/account" className="inline-flex items-center gap-2 rounded-full px-3 sm:px-4 h-11 border text-sm"
                  style={{ borderColor: "var(--border-default)", color: "var(--text-primary)" }}
                  data-testid="nav-account">
              {user.picture ? (
                <img src={user.picture} alt="" className="h-6 w-6 rounded-full" />
              ) : (
                <UserIcon className="h-4 w-4" />
              )}
              <span className="hidden sm:inline">{user.name?.split(" ")[0] || "Account"}</span>
            </Link>
          ) : (
            <button onClick={() => { setAuthMode("signin"); setAuthOpen(true); }}
                    className="inline-flex items-center gap-2 rounded-full px-4 h-11 text-sm text-white"
                    style={{ background: "var(--brand)" }}
                    data-testid="login-btn">
              <LogIn className="h-4 w-4" /> Sign in
            </button>
          )}
        </nav>
      </header>

      {/* Hero */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 sm:pt-16 lg:pt-24 pb-10 grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
        <div className="lg:col-span-8">
          <p className="label-small mb-5 sm:mb-6" data-testid="hero-eyebrow">For curious patients · Not medical advice</p>
          <h1 className="font-serif-display text-[2.5rem] leading-[1.05] sm:text-5xl lg:text-7xl tracking-tight"
              style={{ color: "var(--text-primary)" }} data-testid="hero-headline">
            Understand what your <span style={{ color: "var(--brand)" }}>medications</span> ask of your body.
          </h1>
          <p className="mt-6 sm:mt-8 text-[0.95rem] sm:text-lg max-w-2xl leading-relaxed"
             style={{ color: "var(--text-secondary)" }} data-testid="hero-sub">
            Enter what you&apos;re taking — we&apos;ll surface the labs worth asking about, the foods that help or hurt,
            the interactions to watch, and the organs doing the quiet work behind the scenes.
          </p>
          <div className="mt-6 flex flex-col sm:flex-row sm:flex-wrap gap-3 sm:items-center">
            <Link to="/pricing"
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-full px-6 h-12 text-white"
                  style={{ background: "var(--brand)" }}
                  data-testid="hero-cta-pricing">
              Try 5 days for $5 <ArrowRight className="h-4 w-4" />
            </Link>
            <button
              onClick={() => document.getElementById('analyze-area')?.scrollIntoView({ behavior: 'smooth' })}
              className="label-small sm:text-left hover:opacity-70"
              style={{ color: "var(--text-muted)" }}
              data-testid="hero-free-analysis">
              OR RUN ONE FREE ANALYSIS →
            </button>
          </div>
        </div>
        <div className="lg:col-span-4">
          <div className="rounded-2xl p-5 sm:p-6 border" style={{ background: "var(--bg-secondary)", borderColor: "var(--border-default)" }}>
            <p className="label-small mb-3">How it works</p>
            <ol className="space-y-3 text-sm" style={{ color: "var(--text-secondary)" }}>
              <li className="flex gap-3"><span style={{ color: "var(--brand)" }}>01</span> Add one or more medications.</li>
              <li className="flex gap-3"><span style={{ color: "var(--brand)" }}>02</span> We analyze with up-to-date references.</li>
              <li className="flex gap-3"><span style={{ color: "var(--brand)" }}>03</span> Subscribe to get gentle reminders by email.</li>
            </ol>
          </div>
        </div>
      </section>

      <section id="analyze-area" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-10 sm:pb-16">
        <MedicationInput meds={meds} setMeds={setMeds} notes={notes} setNotes={setNotes} onAnalyze={handleAnalyze} loading={loading} />
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-10">
        <Disclaimer />
      </section>

      <section id="results" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16 sm:pb-24">
        <AnimatePresence mode="wait">
          {loading && (
            <motion.div key="loading" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                        className="rounded-2xl border p-12 text-center" style={{ background: "white", borderColor: "var(--border-default)" }} data-testid="loading-state">
              <p className="label-small mb-4">Analyzing</p>
              <h3 className="font-serif-display text-3xl" style={{ color: "var(--brand)" }}>
                Reading the fine print on your medications<span className="dots"><span>.</span><span>.</span><span>.</span></span>
              </h3>
            </motion.div>
          )}

          {!loading && paywall && (
            <motion.div key="paywall" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                        className="rounded-2xl border p-10 text-center" style={{ background: "var(--brand)", borderColor: "var(--brand)" }} data-testid="paywall">
              <Lock className="h-8 w-8 mx-auto" style={{ color: "white" }} />
              <p className="label-small mt-4" style={{ color: "#A3B19B" }}>YOU&apos;VE USED YOUR FREE ANALYSIS</p>
              <h3 className="font-serif-display text-3xl mt-2" style={{ color: "white" }}>Unlock unlimited insights</h3>
              <p className="mt-3 text-sm max-w-xl mx-auto" style={{ color: "#D6DDD7" }}>
                Continue with a 5-day trial for $5, or go monthly for $30 to also receive personalised email
                reminders about diet, labs and herbal suggestions.
              </p>
              <div className="mt-6 flex flex-wrap gap-3 justify-center">
                <Link to="/pricing" className="rounded-full px-6 py-3" style={{ background: "white", color: "var(--brand)" }} data-testid="paywall-pricing-btn">
                  See plans
                </Link>
                {!user && (
                  <button onClick={() => { setAuthMode("signin"); setAuthOpen(true); }}
                          className="rounded-full px-6 py-3 border" style={{ borderColor: "white", color: "white" }}
                          data-testid="paywall-signin-btn">
                    Sign in or create account
                  </button>
                )}
              </div>
            </motion.div>
          )}

          {!loading && !paywall && result && (
            <motion.div key="result" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
                <div>
                  <p className="label-small mb-2">Your personalised view</p>
                  <h2 className="font-serif-display text-2xl sm:text-3xl md:text-4xl tracking-tight" style={{ color: "var(--brand)" }} data-testid="results-heading">
                    {result.medications.join(" · ")}
                  </h2>
                  {result.summary && <p className="mt-3 max-w-3xl text-sm sm:text-base" style={{ color: "var(--text-secondary)" }} data-testid="results-summary">{result.summary}</p>}
                </div>
                <div className="flex gap-3 w-full sm:w-auto">
                  <button onClick={() => window.print()} className="flex-1 sm:flex-none inline-flex items-center justify-center gap-2 px-5 sm:px-6 h-12 rounded-full border text-sm sm:text-base"
                          style={{ borderColor: "var(--brand)", color: "var(--brand)" }} data-testid="print-summary-btn">
                    Download <ArrowRight className="h-4 w-4" />
                  </button>
                  <button onClick={handleReset} className="flex-1 sm:flex-none inline-flex items-center justify-center gap-2 px-5 sm:px-6 h-12 rounded-full text-white text-sm sm:text-base"
                          style={{ background: "var(--brand)" }} data-testid="new-lookup-btn">New lookup</button>
                </div>
              </div>
              <ResultsBento result={result} />
            </motion.div>
          )}
        </AnimatePresence>
      </section>

      <footer className="border-t" style={{ borderColor: "var(--border-default)" }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <p className="label-small" data-testid="footer-brand">SafeRxCheck · An educational tool</p>
          <p className="text-xs max-w-xl leading-relaxed" style={{ color: "var(--text-muted)" }} data-testid="footer-disclaimer">
            <AlertTriangle className="inline h-3.5 w-3.5 mr-1 align-text-bottom" />
            Information provided is for general education and is not a substitute for diagnosis or treatment from a licensed clinician.
          </p>
        </div>
      </footer>

      <AuthModal open={authOpen} onClose={() => setAuthOpen(false)} initialMode={authMode} />
    </div>
  );
}
